import type { ReactNode } from 'react';
import { ArrowUpRight, Pencil, Plus, Target } from 'lucide-react';
import { Link } from 'react-router-dom';

import { dateLabel, OPPORTUNITY_STATUS_LABEL } from '../../../lib/crm/opportunitySheet';
import type { SheetOpportunity } from '../../../hooks/useOpportunitySheet';
import { BTN_GHOST, BTN_PRIMARY } from '../../bureau/buttons';
import { REF } from '../../bureau/encre';

/**
 * The head of a deal's sheet (mock-up « Encre-Fiche »): the deal's mark on Encre's disc, its name,
 * « Cars du Nord · Proposition » — the account opens its own sheet —, a closed deal said as such
 * with its day; then the gestures: give Otto a mission about it (the blue), close or reopen it
 * (`outcome`), edit it here, open the full record.
 */
export function OpportunitySheetHeader({
  deal,
  outcome,
  newMissionHref,
  ficheHref,
  editing,
  onOpenAccount,
  onEdit,
  onNavigate,
}: {
  deal: SheetOpportunity;
  /** « Gagnée » / « Perdue », or « Rouvrir » on a closed deal. */
  outcome: ReactNode;
  newMissionHref: string;
  ficheHref: string;
  /** The form is open: no gesture that would leave it (the form saves or cancels). */
  editing: boolean;
  onOpenAccount: (id: string) => void;
  onEdit: () => void;
  onNavigate: () => void;
}) {
  const closedOn = deal.status !== 'open' ? dateLabel(deal.close_date) : null;
  const account = deal.accountRef;
  return (
    <header className="flex flex-col gap-5 pb-6">
      <div className="flex items-center gap-4">
        <span aria-hidden="true" className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-desk-accent-soft text-desk-accent-text">
          <Target size={24} strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-medium leading-8 tracking-[-0.02em] text-desk-ink">{deal.name}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-desk-soft">
            {account &&
              (editing ? (
                <span>{account.name}</span>
              ) : (
                <button type="button" onClick={() => onOpenAccount(account.id)} className={REF}>
                  {account.name}
                </button>
              ))}
            {account && deal.stage && <span aria-hidden="true">·</span>}
            {deal.stage && <span>{deal.stage.name}</span>}
            {deal.status !== 'open' && (
              <span className={deal.status === 'won' ? 'font-medium text-desk-accent-text' : 'text-desk-muted'}>
                · {OPPORTUNITY_STATUS_LABEL[deal.status].toLowerCase()}
                {closedOn && ` le ${closedOn}`}
              </span>
            )}
          </p>
        </div>
      </div>
      {/* While editing, nothing here leaves the form: its own buttons save or cancel. */}
      {!editing && (
        <div className="flex flex-wrap items-center gap-2">
          <Link to={newMissionHref} onClick={onNavigate} className={BTN_PRIMARY}>
            <Plus size={16} aria-hidden="true" /> Nouvelle mission
          </Link>
          {outcome}
          <button type="button" onClick={onEdit} className={BTN_GHOST}>
            <Pencil size={15} aria-hidden="true" /> Modifier
          </button>
          <Link to={ficheHref} onClick={onNavigate} className={BTN_GHOST}>
            Fiche complète <ArrowUpRight size={16} aria-hidden="true" />
          </Link>
        </div>
      )}
    </header>
  );
}
